import React, { ReactElement, useCallback, useEffect, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import isEqual from 'lodash.isequal';
import { compose } from 'redux';
import { StickyHeaderProvider } from 'b2b-apps/core/common/context/StickyHeaderProvider';
import { getShouldInitContextSync } from 'b2b-apps/core/common/utils/syncContext';
import { useSyncContext } from 'b2b-apps/core/context/hooks/useSyncContext';
import { useHotJarTracking } from 'b2b-apps/shop/analytics/hooks';
import { useClearSuggestionsAction } from 'b2b-apps/shop/catalog/hooks';
import { RESOLUTION_TYPE, useDeviceType, useScrollLock, useToggle } from 'b2b-apps/shop/common/hooks';
import { isMobile } from 'b2b-apps/shop/common/utils/env';
import withScrollRestore from '../withScrollRestore';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    useHotJarTracking();

    const deviceType = useDeviceType();
    const clearSuggestions = useClearSuggestionsAction();
    const [isAutosuggestOpened, toggleIsAutosuggestOpened] = useToggle();
    const autosuggestRef = useRef<HTMLDivElement | null>(null);
    const scrollLock = useScrollLock();

    const handleAutosuggestRefCallback = useCallback(
      (autosuggestBox: HTMLDivElement | null): void => {
        scrollLock(autosuggestBox, RESOLUTION_TYPE.mobile);
        autosuggestRef.current = autosuggestBox;
      },
      [scrollLock],
    );

    const handleOpenAutosuggest = useCallback(
      (): void => {
        if (!isAutosuggestOpened) {
          toggleIsAutosuggestOpened(true);
        }
      },
      [isAutosuggestOpened, toggleIsAutosuggestOpened],
    );

    const handleCloseAutosuggest = useCallback(
      (): void => {
        if (isAutosuggestOpened) {
          toggleIsAutosuggestOpened(false);
          clearSuggestions();
        }
      },
      [clearSuggestions, isAutosuggestOpened, toggleIsAutosuggestOpened],
    );

    const handleScroll = useCallback(
      (): void => {
        // FIX for iPhones - despite hidden vertical scrolling when the autosuggest box is open
        // sometimes is possible to scrolling, therefore closing box is forced
        if (autosuggestRef.current) {
          clearAllBodyScrollLocks();
          toggleIsAutosuggestOpened(false);
        }
      },
      [toggleIsAutosuggestOpened],
    );

    useEffect(
      (): () => void => {
        if (isMobile(deviceType)) {
          window.addEventListener('scroll', handleScroll);
        }

        return () => {
          window.removeEventListener('scroll', handleScroll);
        };
      },
      [deviceType, handleScroll],
    );

    const triggerSyncContext = useSyncContext();

    useEffect(
      () => {
        if (getShouldInitContextSync()) {
          triggerSyncContext();
        }
      },
      [],
    );

    const injectedProps: InjectedProps = {
      autosuggestRefCallback: handleAutosuggestRefCallback,
      isAutosuggestOpened,
      onOpenAutosuggest: handleOpenAutosuggest,
      onCloseAutosuggest: handleCloseAutosuggest,
    };

    return (
      <StickyHeaderProvider>
        <Component {...props} {...injectedProps} />
      </StickyHeaderProvider>
    );
  };

  return compose<EnhancedComponentType>(
    withScrollRestore,
    withRouter,
  )(
    React.memo(
      WithContainer,
      (prevProps, nextProps) => isEqual(prevProps, nextProps),
    ),
  );
};

export { withContainer };
